import * as React from 'react';
import {
    Button, Checkbox, CheckboxProps, Input, InputProps,
} from 'semantic-ui-react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { getColumnHeaderLabel } from 'author/src/features/addQuestions/services/aiAssistedMarkingService';
import storeManager from 'author/src/store';
import { getAppSpecifiedSearchQueryBuilderService, AppSpecifiedSearchQueryBuildService } from '../../../../services/search/commonSearchQueryBuilder';
import { setContextMenuVisibility } from '../../../../store/workspaces/actions';
import { tl } from '../../../../services/i18nService';
import commonSearchHitService, {
    getAppSpecifiedSearchHitService, AppSpecifiedSearchHitService,
} from '../../../../services/search/commonSearchHitService';
import { HitData, SearchColumn } from '../../../../store/common/search/types';
import {
    removeColumnFromList,
    restoreDefaultList,
    toggleColumnFromList,
    updateList,
} from '../../actions';
import { TableState } from '../../reducer';
import { CommonContextState } from '../../../../store/context/types';
import { CommonRootState } from '../../../../store/typings';
import { getAppSpecifiedPermissionService, AppSpecifiedPermissionService } from '../../../../services/permissions/commonPermissionService';
import * as styles from './ColumnPicker.scss';
import ColumnCard from './ColumnCard';
import { buildColumnTitleUpdater, toOriginalTitle } from '../../../../services/updateTitleService';

interface StateProps {
    tableState: TableState;
    contextState: CommonContextState;
    userId: number;
}

interface DispatchProps {
    onUpdateOrder: (featureId: string, dragIndex: number, hoverIndex: number) => void;
    onRemoveColumn: (featureId: string, columnRef: string) => void;
    onToggleColumn: (featureId: string, columnRef: string) => void;
    onRestoreDefaultList?: (featureId: string) => void;
    setContextMenuVisibility?: (featureId: string) => void;
    onCloseCallback?: (featureId: string) => void;
    cardDescriptionRenderer?: (hit: any, searchColumn: SearchColumn) => React.ReactNode;
}

interface OwnProps {
    featureId: string;
    defaultColumns: any[];
}

type ComponentStateProps = StateProps & OwnProps;

type ComponentProps = ComponentStateProps & DispatchProps;

interface State {
    search: string;
}

export class ColumnPicker extends React.PureComponent<ComponentProps, State> {
    constructor(props: ComponentProps) {
        super(props);
        this.state = {
            search: '',
        };
    }

    componentDidMount() {
        const { featureId, setContextMenuVisibility } = this.props;
        if (typeof setContextMenuVisibility === 'function') {
            setContextMenuVisibility(featureId);
        }
    }

    componentWillUnmount() {
        const { featureId, setContextMenuVisibility } = this.props;
        if (typeof setContextMenuVisibility === 'function') {
            setContextMenuVisibility(featureId);
        }
    }

    handleSearch = (e: React.SyntheticEvent, data: InputProps) => {
        this.setState({
            search: data.value,
        });
    };

    buildTitleUpdater = () => {
        const rootState = storeManager?.getStore()?.getState();
        const featureState = rootState ? rootState[this.props.featureId] : {};
        return buildColumnTitleUpdater(featureState);
    };

    handleToggle = (e: React.SyntheticEvent, data: CheckboxProps) => {
        const { featureId, onToggleColumn } = this.props;
        onToggleColumn(featureId, toOriginalTitle(data) as string);
    };

    handleRemove = (id: string) => {
        const { featureId, onRemoveColumn } = this.props;
        onRemoveColumn(featureId, id);
    };

    handlePositionChange = (dragIndex: number, hoverIndex: number) => {
        const { featureId, onUpdateOrder } = this.props;
        onUpdateOrder(featureId, dragIndex, hoverIndex);
    };

    getIndexInFullByRef = (columnRef: string): number => {
        const { defaultColumns, tableState } = this.props;
        const columns = tableState.customList?.columns ? tableState.customList.columns : defaultColumns;
        return columns.findIndex((column: any) => column.ref === columnRef);
    };

    handleRestoreDefaultList = () => {
        const { featureId, onRestoreDefaultList } = this.props;
        onRestoreDefaultList(featureId);
    };

    handleOnCloseCallback = () => {
        const { featureId, onCloseCallback } = this.props;
        onCloseCallback(featureId);
    };

    getCurrentValue = (columnRef: string) => {
        const {
            tableState, contextState, userId, featureId, cardDescriptionRenderer,
        } = this.props;
        const { dataType, hits } = tableState;
        let value: any | any[] = null;

        if (hits.length) {
            const appSpecifiedSearchHitService: AppSpecifiedSearchHitService = getAppSpecifiedSearchHitService();
            const appSpecifiedSearchQueryBuilderService: AppSpecifiedSearchQueryBuildService = getAppSpecifiedSearchQueryBuilderService();
            const fields: SearchColumn[] = appSpecifiedSearchQueryBuilderService
                .getColumnsDefinition(dataType, featureId)
                .map(this.buildTitleUpdater());

            const searchColumn = fields.find((searchColumn: SearchColumn) => searchColumn.ref === columnRef);
            const key: string = appSpecifiedSearchHitService.getHitSelectionKey(dataType);
            const hit: HitData = hits.find((hit: HitData) => hit[key] === (contextState as any)[key]);

            if (typeof cardDescriptionRenderer === 'function') {
                return cardDescriptionRenderer(hit || hits[0], searchColumn);
            }
            value = commonSearchHitService.getColumnValueFromHit(hit || hits[0], searchColumn, userId);
            if (Array.isArray(value)) {
                return value.join(',');
            }
        }
        return value;
    };

    putSelectedColumnsOnTop = (searchColumnA: SearchColumn, searchColumnB: SearchColumn): number => {
        const { tableState } = this.props;
        const { defaultColumns, customList } = tableState;
        let activeA: boolean;
        let activeB: boolean;
        if (customList && customList.columns) {
            activeA = customList.columns.some((column: any) => searchColumnA.ref === column.ref);
            activeB = customList.columns.some((column: any) => searchColumnB.ref === column.ref);
        } else {
            activeA = defaultColumns.some((column: any) => searchColumnA.ref === column.ref);
            activeB = defaultColumns.some((column: any) => searchColumnB.ref === column.ref);
        }
        if (activeA && !activeB) {
            return -1;
        }
        if (!activeA && activeB) {
            return 1;
        }
        return 0;
    };

    filterBySearchText = (searchColumn: SearchColumn) => {
        if (this.state.search) {
            const str: string = getColumnHeaderLabel(searchColumn.ref);
            return !!str.toLowerCase().includes(this.state.search.toLowerCase());
        }
        return true;
    };

    filterNotHiddenInPicker = (searchColumn: SearchColumn) => {
        return !searchColumn.hideInColumnPicker;
    };

    renderCheckboxes = (searchColumn: SearchColumn) => {
        const { tableState } = this.props;
        const updateTitleColumn = this.buildTitleUpdater();
        const customList = tableState.customList
            ? { ...tableState.customList, columns: tableState.customList?.columns?.map(updateTitleColumn) }
            : tableState.customList;
        const defaultColumns = tableState.defaultColumns.map(updateTitleColumn);
        let checked: boolean;
        if (customList && customList.columns) {
            checked = customList.columns.some((column: any) => searchColumn.ref === column.ref);
        } else {
            checked = defaultColumns.some((column: any) => searchColumn.ref === column.ref);
        }
        const label = searchColumn.fieldDefinitionId
            ? searchColumn.name
            : getColumnHeaderLabel(searchColumn.ref);
        return (
            <Checkbox
                key={searchColumn.ref}
                checked={checked}
                id={searchColumn.ref}
                onChange={this.handleToggle}
                label={label}
            />
        );
    };

    getColumnsForDisplay = (isRenderedInPickerList?: boolean) => {
        const { defaultColumns, tableState, featureId } = this.props;
        const { customList, dataType } = tableState;
        const appSpecifiedPermissionService: AppSpecifiedPermissionService = getAppSpecifiedPermissionService();
        const appSpecifiedSearchQueryBuilderService: AppSpecifiedSearchQueryBuildService = getAppSpecifiedSearchQueryBuilderService();
        const columnsDefinition = appSpecifiedSearchQueryBuilderService.getColumnsDefinition(dataType, featureId);
        let columnsForRender = customList?.columns ? customList.columns : defaultColumns;

        if (isRenderedInPickerList) {
            columnsForRender = columnsDefinition
                .map((column: any, columnIndex: number) => ({ ...column, columnIndex }))
                .filter((searchColumn: SearchColumn) => !searchColumn.hideInColumnPicker);
        } else {
            columnsForRender = columnsForRender
                .map((column: any, columnIndex: number) => ({ ...column, columnIndex }))
                .filter((column: any) => {
                    const searchColumn: SearchColumn = columnsDefinition.find((columnDef: any) => columnDef.ref === column.ref);

                    return !searchColumn?.hideInColumnPicker;
                });
        }

        return appSpecifiedPermissionService.filterColumnsByPermissions(columnsForRender, dataType, featureId).map(this.buildTitleUpdater());
    };

    renderColumnsPickerList = () => {
        return this.getColumnsForDisplay(true)
            .filter(this.filterBySearchText)
            .sort(this.putSelectedColumnsOnTop)
            .map(this.renderCheckboxes);
    };

    renderColumnCards = () => {
        const { tableState, featureId } = this.props;
        const { dataType } = tableState;
        const appSpecifiedSearchQueryBuilderService: AppSpecifiedSearchQueryBuildService = getAppSpecifiedSearchQueryBuilderService();
        const columnsDefinition = appSpecifiedSearchQueryBuilderService.getColumnsDefinition(dataType, featureId).map(this.buildTitleUpdater());
        const columnsForDisplay = this.getColumnsForDisplay();

        return columnsForDisplay.map((column: any) => {
            const searchColumn: SearchColumn = columnsDefinition.find((columnDef: any) => columnDef.ref === column.ref);

            return (
                <ColumnCard
                    key={column.ref}
                    column={searchColumn}
                    getIndexInFullByRef={this.getIndexInFullByRef}
                    description={this.getCurrentValue(column.ref)}
                    moveCard={this.handlePositionChange}
                    onRemove={this.handleRemove}
                />
            );
        });
    };

    render() {
        const { onRestoreDefaultList, onCloseCallback } = this.props;
        const columnsForDisplay = this.getColumnsForDisplay();

        return (
            <div className={styles.container}>
                <div className={styles.fieldsPanel}>
                    <div className={styles.fieldsContainer}>
                        <Input onChange={this.handleSearch} value={this.state.search} />
                        {this.renderColumnsPickerList()}
                    </div>
                </div>
                <div className={styles.mainContainer}>
                    <div className={styles.flexParentContainer}>
                        <div><h3>{`${tl('common.columnPicker.selectedCount')} (${columnsForDisplay?.length})`}</h3></div>
                        {
                            typeof onRestoreDefaultList === 'function'
                                ? (
                                    <Button
                                        onClick={this.handleRestoreDefaultList}
                                    >
                                        {tl('common.columnPicker.restoreDefaults')}
                                    </Button>
                                )
                                : null
                        }
                        {
                            typeof onCloseCallback === 'function'
                                ? (
                                    <Button
                                        onClick={this.handleOnCloseCallback}
                                    >
                                        {tl('general.close')}
                                    </Button>
                                )
                                : null
                        }
                    </div>
                    <div className={styles.dndContainer}>
                        {this.renderColumnCards()}
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: CommonRootState, ownProps: OwnProps): ComponentStateProps => ({
    tableState: state[ownProps.featureId],
    contextState: state.context,
    userId: state.session.user.id,
    ...ownProps,
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => bindActionCreators(
    {
        setContextMenuVisibility,
        onRestoreDefaultList: restoreDefaultList,
        onUpdateOrder: updateList,
        onRemoveColumn: removeColumnFromList,
        onToggleColumn: toggleColumnFromList,
    },
    dispatch,
);

export default connect<StateProps, DispatchProps>(mapStateToProps, mapDispatchToProps)(ColumnPicker);
